// The art for the worker's setup sections (worker/components/OnboardingPro.jsx,
// the Monzo-style section hub and the intro screen in front of each section).
// Same house style and the same two sets as shared/onboardingSectionArt.js (the
// Business app's), drawn for the worker:
//   HEROES  160 by 120, free standing (no disc behind them), big on an intro
//   CARDS   72 by 72, the small picture on the left of a section card
//
// Signing and the final checks mean the same thing in both apps, so they are the
// Business pictures, not copies of them.
//
// ⛔ Colours are {{slot}}s filled from the pastel tones only (fillIcon in
// shared/announcementIcons.js): never turquoise (the logo) or the link blue
// (actions). Decorative: the screen carries the words.
//
// XML, not JSX, so the same source renders in the app (SvgXml) and to PNG for
// review (practice/scripts/preview-section-art.mjs).

import { dot, lift, line, path, rect, sparkle } from './howItWorksArt.js'
import { SECTION_CARD_XML, SECTION_HERO_XML } from './onboardingSectionArt.js'

const card = (x, y, w, h, r) => (f) => rect(x, y, w, h, r, f)
const turn = (deg, cx, cy, body) => `<g transform="rotate(${deg} ${cx} ${cy})">${body}</g>`
const at = (dx, dy, body) => `<g transform="translate(${dx} ${dy})">${body}</g>`
// A pound sign drawn round (0, 0), for a coin.
const POUND = (stroke, w = 2.6) => line('M5 -7c-1-2-3-3-5-3-3 0-5 2-5 5v12m-3-6h9m-9 6h13', stroke, w)
// Head and shoulders, centred on (x, y), for a photo or a selfie.
const person = (x, y, s, fill) =>
  dot(x, y - 4 * s, 5 * s, fill) + path(`M${x - 9 * s} ${y + 9 * s}c0-7 4-10 ${9 * s}-10s${9 * s} 3 ${9 * s} 10z`)(fill)

const HEROES = {
  // Welcome, once, between the account and the hub: their phone with a shift on
  // it, the wallet it pays into with a coin dropping in, and confetti.
  welcome:
    turn(-6, 66, 58,
      lift(card(40, 6, 54, 104, 11), '{{lavender}}', 4, 4)
      + rect(46, 18, 42, 82, 6, '{{white}}') + rect(58, 11, 18, 3, 1.5, '{{white}}', 0.7)
      + lift(card(50, 26, 34, 26, 5), '{{mint}}', 2, 2)
      + dot(58, 34, 3.5, '{{white}}') + rect(64, 32, 16, 4, 2, '{{white}}') + rect(55, 42, 18, 4, 2, '{{white}}', 0.7)
      + rect(50, 60, 34, 6, 3, '{{softPink}}', 0.8) + rect(50, 72, 24, 6, 3, '{{softPink}}', 0.6)
      + rect(50, 84, 30, 9, 4.5, '{{yellow}}'))
    + lift(card(98, 66, 50, 38, 9), '{{coral}}', 4, 4)
    + lift(card(124, 76, 28, 18, 6), '{{lime}}', 2, 2) + dot(134, 85, 3.5, '{{white}}')
    + lift((f) => dot(122, 44, 14, f), '{{yellow}}', 3, 3) + at(122, 44, `<g transform="scale(0.85)">${POUND('{{ink}}', 3)}</g>`)
    + turn(24, 20, 36, rect(16, 34, 9, 4, 2, '{{pink}}')) + turn(-30, 146, 26, rect(142, 24, 9, 4, 2, '{{mint}}'))
    + turn(40, 18, 92, rect(14, 90, 9, 4, 2, '{{yellow}}')) + turn(-18, 104, 14, rect(100, 12, 9, 4, 2, '{{coral}}'))
    + dot(30, 62, 2.6, '{{lavender}}') + dot(152, 52, 2.4, '{{pink}}') + dot(92, 112, 2.4, '{{coral}}')
    + sparkle(26, 16, 7, '{{yellow}}') + sparkle(148, 110, 5, '{{pink}}') + sparkle(140, 8, 5, '{{lavender}}'),

  // Your account: the phone, a code arriving, and the padlock their PIN closes.
  account:
    lift(card(54, 8, 50, 100, 10), '{{lavender}}', 4, 4)
    + rect(60, 20, 38, 76, 6, '{{white}}') + rect(71, 13, 16, 3, 1.5, '{{white}}', 0.7)
    + [0, 1, 2, 3].map((i) => dot(67 + i * 8, 46, 3, '{{lavender}}')).join('')
    + [0, 1, 2].map((r) => [0, 1, 2].map((c) => rect(64 + c * 11, 58 + r * 10, 8, 6, 2, '{{softPink}}', 0.8)).join('')).join('')
    + lift((f) => rect(12, 28, 50, 26, 11, f) + path('M24 52l-4 10 12-8z')(f), '{{yellow}}', 3, 3)
    + [0, 1, 2, 3, 4].map((i) => dot(24 + i * 7, 41, 2.6, '{{ink}}')).join('')
    + line('M112 66v-8a10 10 0 0 1 20 0v8', '{{depth}}', 6).replace('<path', '<path transform="translate(3 3)"')
    + line('M112 66v-8a10 10 0 0 1 20 0v8', '{{coral}}', 6)
    + lift(card(104, 64, 36, 30, 7), '{{coral}}', 3, 3)
    + dot(122, 76, 4, '{{ink}}') + rect(120.5, 77, 3, 8, 1.5, '{{ink}}')
    + sparkle(30, 16, 7, '{{pink}}') + sparkle(140, 22, 6, '{{yellow}}') + sparkle(26, 98, 5, '{{mint}}'),

  // About you: an ID card with their photo, and the selfie that matches it.
  aboutYou:
    turn(-6, 68, 64,
      lift(card(18, 36, 96, 60, 9), '{{mint}}', 4, 4)
      + rect(28, 48, 28, 36, 5, '{{white}}') + person(42, 66, 1, '{{softPink}}')
      + rect(64, 50, 38, 6, 3, '{{white}}') + rect(64, 62, 28, 5, 2.5, '{{white}}', 0.7)
      + rect(64, 72, 34, 5, 2.5, '{{white}}', 0.7))
    + lift((f) => dot(120, 44, 24, f), '{{coral}}', 3, 3)
    + `<clipPath id="wsaSelfie"><circle cx="120" cy="44" r="24"/></clipPath>`
    + `<g clip-path="url(#wsaSelfie)">${person(120, 48, 1.5, '{{yellow}}')}</g>`
    + lift((f) => dot(138, 66, 10, f), '{{lime}}', 2, 2) + line('M133 66l3.5 3.5 6-6.5', '{{ink}}', 2.6)
    + sparkle(22, 20, 8, '{{yellow}}') + sparkle(146, 102, 5, '{{pink}}') + sparkle(86, 18, 4, '{{lavender}}'),

  // Your work: the bag they bring, and the badge that says they are qualified.
  work:
    line('M64 34v-8a7 7 0 0 1 7-7h18a7 7 0 0 1 7 7v8', '{{depth}}', 7).replace('<path', '<path transform="translate(3 3)"')
    + line('M64 34v-8a7 7 0 0 1 7-7h18a7 7 0 0 1 7 7v8', '{{lavender}}', 7)
    + lift(card(30, 34, 100, 66, 10), '{{coral}}', 4, 4)
    + rect(30, 58, 100, 9, 0, '{{softPink}}')
    + lift(card(70, 52, 20, 18, 4), '{{yellow}}', 2, 2)
    + path('M114 96l-8 20 9-4 5 7 5-17z')('{{pink}}') + path('M134 96l8 20-9-4-5 7-5-17z')('{{pink}}')
    + lift((f) => dot(124, 90, 17, f), '{{lavender}}', 3, 3)
    + dot(124, 90, 11, '{{white}}') + line('M118.5 90l4 4 7-7.5', '{{ink}}', 2.8)
    + sparkle(22, 26, 8, '{{yellow}}') + sparkle(142, 22, 5, '{{mint}}') + sparkle(18, 104, 5, '{{pink}}'),

  // Getting paid: a wallet with a card tucked in and a coin on its way.
  gettingPaid:
    turn(-10, 76, 44, lift(card(44, 22, 66, 42, 7), '{{lavender}}', 3, 3) + rect(52, 30, 13, 10, 2.5, '{{yellow}}') + rect(52, 50, 22, 4, 2, '{{white}}', 0.8))
    + lift(card(26, 46, 96, 60, 11), '{{mint}}', 4, 4)
    + lift(card(92, 62, 40, 28, 9), '{{lime}}', 2, 2) + dot(106, 76, 4.5, '{{white}}')
    + lift((f) => dot(136, 34, 16, f), '{{yellow}}', 3, 3) + at(136, 34, POUND('{{ink}}'))
    + lift((f) => dot(30, 100, 10, f), '{{yellow}}', 2, 2) + at(30, 100, `<g transform="scale(0.62)">${POUND('{{ink}}', 3.6)}</g>`)
    + sparkle(22, 26, 8, '{{pink}}') + sparkle(150, 70, 5, '{{lavender}}') + sparkle(108, 14, 4, '{{coral}}'),

  // The final checks (our worker terms): the Business signing picture (a page, a pen, a seal).
  signing: null,

  // Ready to work: their pass, ticked, with confetti in the air.
  ready:
    turn(-5, 80, 62,
      lift(card(26, 30, 108, 66, 11), '{{lavender}}', 4, 4)
      + dot(52, 58, 14, '{{white}}') + `<clipPath id="wsaPass"><circle cx="52" cy="58" r="14"/></clipPath>`
      + `<g clip-path="url(#wsaPass)">${person(52, 62, 1, '{{softPink}}')}</g>`
      + rect(74, 46, 46, 7, 3.5, '{{white}}') + rect(74, 59, 32, 5, 2.5, '{{white}}', 0.7)
      + rect(38, 80, 40, 7, 3.5, '{{yellow}}'))
    + lift((f) => dot(128, 90, 18, f), '{{lime}}', 3, 3) + line('M119 90l6.5 6.5 11-12', '{{ink}}', 3.4)
    + turn(24, 18, 40, rect(14, 38, 9, 4, 2, '{{pink}}')) + turn(-30, 146, 30, rect(142, 28, 9, 4, 2, '{{yellow}}'))
    + turn(40, 14, 86, rect(10, 84, 9, 4, 2, '{{mint}}')) + turn(-18, 98, 12, rect(94, 10, 9, 4, 2, '{{coral}}'))
    + dot(60, 14, 2.6, '{{lavender}}') + dot(150, 56, 2.4, '{{pink}}') + dot(30, 110, 2.4, '{{coral}}')
    + sparkle(30, 18, 7, '{{yellow}}') + sparkle(148, 104, 5, '{{pink}}') + sparkle(118, 16, 5, '{{mint}}'),

  // We're confirming your details: the Business final checks picture.
  checking: null,
}

const CARDS = {
  // Your account: a phone with a padlock.
  account:
    lift(card(16, 8, 30, 54, 6), '{{lavender}}')
    + rect(20, 15, 22, 38, 3, '{{white}}')
    + [0, 1, 2, 3].map((i) => dot(24.5 + i * 4.5, 30, 1.6, '{{lavender}}')).join('')
    + line('M46 40v-5a6 6 0 0 1 12 0v5', '{{coral}}', 3.6)
    + lift(card(40, 39, 24, 20, 5), '{{coral}}', 2, 2) + dot(52, 48, 2.4, '{{ink}}')
    + sparkle(60, 14, 5, '{{yellow}}'),

  // About you: an ID card and a selfie.
  aboutYou:
    lift(card(6, 18, 46, 32, 5), '{{mint}}')
    + rect(11, 24, 14, 20, 3, '{{white}}') + dot(18, 31, 3.4, '{{softPink}}')
    + path('M12.5 42c0-4 2.5-6 5.5-6s5.5 2 5.5 6z')('{{softPink}}')
    + rect(29, 27, 18, 4, 2, '{{white}}') + rect(29, 35, 13, 4, 2, '{{white}}', 0.7)
    + lift((f) => dot(52, 50, 13, f), '{{coral}}', 2, 2)
    + `<clipPath id="wscSelfie"><circle cx="52" cy="50" r="13"/></clipPath>`
    + `<g clip-path="url(#wscSelfie)">${person(52, 53, 0.85, '{{yellow}}')}</g>`
    + sparkle(60, 14, 5, '{{lavender}}'),

  // Your work: a bag and a badge.
  work:
    line('M27 24v-4a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v4', '{{lavender}}', 4)
    + lift(card(10, 24, 50, 34, 6), '{{coral}}')
    + rect(10, 36, 50, 5, 0, '{{softPink}}') + rect(30, 33, 10, 9, 2, '{{yellow}}')
    + lift((f) => dot(56, 54, 10, f), '{{lavender}}', 2, 2) + dot(56, 54, 6, '{{white}}')
    + line('M53 54l2.2 2.2 4-4.4', '{{ink}}', 2),

  // Getting paid: a wallet and a coin.
  gettingPaid:
    lift(card(8, 26, 50, 34, 7), '{{mint}}')
    + lift(card(40, 35, 22, 16, 5), '{{lime}}', 2, 2) + dot(48, 43, 2.6, '{{white}}')
    + lift((f) => dot(46, 16, 10, f), '{{yellow}}', 2, 2) + at(46, 16, `<g transform="scale(0.6)">${POUND('{{ink}}', 3.6)}</g>`)
    + sparkle(14, 14, 5, '{{pink}}'),
}

const svg = (w, h, body) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}">${body}</svg>`

export const WORKER_SECTION_HERO_XML = {
  ...Object.fromEntries(Object.entries(HEROES).filter(([, b]) => b).map(([k, b]) => [k, svg(160, 120, b)])),
  signing: SECTION_HERO_XML.signing,
  checking: SECTION_HERO_XML.checking,
}
export const WORKER_SECTION_CARD_XML = {
  ...Object.fromEntries(Object.entries(CARDS).map(([k, b]) => [k, svg(72, 72, b)])),
  signing: SECTION_CARD_XML.signing,
}
